import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { ChangeDetectorRef, Component, ElementRef, HostBinding, Input, OnDestroy, OnInit, Optional, Self, ViewChild } from '@angular/core';
import { UntypedFormControl, NgControl } from '@angular/forms';
import { MatAutocomplete, MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Observable, of, Subscription } from 'rxjs';
import { debounceTime, filter, finalize, switchMap, tap, map } from 'rxjs/operators';
import { ApiService } from 'src/lib/services/api.service';
import { MatFormFieldBaseComponent } from 'src/lib/mat-form-field.component';

type User = {
	id?: number
	guid?: string
	matricule?: string
	picture?: string
	nomComplet?: string
	fonctionAndVille?: string
	connection?: any
	isActive?: boolean
}

@Component({
	selector: 'mat-user-autocomplete',
	templateUrl: './mat-user-autocomplete.component.html',
	providers: [{provide: MatFormFieldControl, useExisting: MatUserAutocompleteComponent}]
})
export class MatUserAutocompleteComponent extends MatFormFieldBaseComponent<User> implements OnInit, OnDestroy {

	static nextId: number = 0;

	@Input() @HostBinding() id = `mat-user-autocomplete-${MatUserAutocompleteComponent.nextId++}`;
	@ViewChild('inputElement') inputElementRef: ElementRef<HTMLInputElement>;

	@Input() placeholder: string = 'Rechercher...';

	@Input()
	get disabled () { return super.disabled; }
	set disabled(value: any) {
		super.disabled = value;
		
		if(value) this.inputControl.disable();
		else this.inputControl.enable();
	}

	@Input() onlyEtablissements: number[];

	@Input()
	get onlyEtablissement(): number { return this.onlyEtablissements[0] ?? null;  }
	set onlyEtablissement(value: any) { this.onlyEtablissements = [coerceNumberProperty(value)]; this.stateChanges.next(); }

	private _withUsers: boolean | Observable<User[]> = false;
	@Input()
	get withUsers() { return this._withUsers; }
	set withUsers(value: any) { 

		if(value instanceof Observable)
			this._withUsers = value;
		else 
			this._withUsers = coerceBooleanProperty(value);
			
		this.stateChanges.next();
	}

	_disableWithoutGUID: boolean = false;
	@Input()
	get disableWithoutGUID() { return this._disableWithoutGUID; }
	set disableWithoutGUID(value: any) { 
		this._disableWithoutGUID = coerceBooleanProperty(value);			
		this.stateChanges.next();
	}

	inputState: {isWaiting: boolean, isTyping: boolean} = {isWaiting: false, isTyping: false};
	inputControl: UntypedFormControl = new UntypedFormControl();
	inputControlSubscription: Subscription;
	inputList: User[] = [];

	constructor(@Optional() @Self() public ngControl: NgControl,
				public focusMonitor: FocusMonitor,
				public elementRef: ElementRef<HTMLElement>,
				private changeDetectorRef: ChangeDetectorRef,
				private apiService: ApiService) { super(ngControl, focusMonitor, elementRef); }

	get empty() {
		return super.empty && !this.inputControl.value?.length;
	}

	get shouldHideInput (): boolean {
		return (this.disabled || (!this.multiple && !!(this.value as User)));
	}

	ngOnInit(): void {
		const searchEndpoint = (typeof this._withUsers === 'boolean' && this._withUsers === true) ? 'users' : 'salaries';

		this.inputControlSubscription = 
		this.inputControl.valueChanges.pipe(
				tap(() => this.inputState.isTyping = true),
				debounceTime(300),
				filter(terms => terms && terms.length > 2),
				tap(() => {
					this.inputList = [];
					this.inputState.isWaiting = true;
				}),
				switchMap(terms => this.onlyEtablissements?.length ? 
					this.apiService.post(`${searchEndpoint}/search/${terms}`, {etablissements: this.onlyEtablissements}) :
					this.apiService.get(`${searchEndpoint}/search/${terms}`)
				),

                map(items => items.map(({id, guid, isActive, fonctionAndVille, mail, matricule, nom, nomComplet, picture}) => 
                    ({id, guid, isActive, fonctionAndVille, mail, matricule, nom, nomComplet, picture}))),

				switchMap(items => (
					this._withUsers instanceof Observable ? this._withUsers : of([])).pipe(
						map(users => [...items, ...users]),
						finalize(() => this.inputState = {isWaiting: false, isTyping: false})
					)
				)
			)
			.subscribe(items => this.inputList = items)
	}

	ngOnDestroy () {
		super.ngOnDestroy();
		this.inputControlSubscription.unsubscribe();
	}

	private isAlreadySelected ({guid}: User): boolean {
		return (this._values ?? []).map(({guid}) => guid).includes(guid);
	}

	onSelectUser (event: MatAutocompleteSelectedEvent) {

		this.inputControl.reset(null, {emitEvent: false});

		if(!this.isAlreadySelected(event.option.value)) {

			if(this.multiple) {
				if(!this.value)
					this.value = [];
			
				(this.value as User[]).push(event.option.value);
			} else
				(this.value as User) = event.option.value;

			this.changeDetectorRef.detectChanges();
			super.onChange();
		}
	}

	onDeselectUser (user: User) {
		
		if(this.multiple)
			(this.value as User[]).splice((this.value as User[]).findIndex(({guid}) => guid === user.guid), 1);
		else
			this.value = null;

		this.changeDetectorRef.detectChanges();
		super.onChange();
	}

	onContainerClick(): void { this.inputElementRef?.nativeElement?.focus(); }

    onOpened (autocompleteElementRef: MatAutocomplete) {

        if(autocompleteElementRef.panel)
            autocompleteElementRef.panel.nativeElement.closest('.cdk-overlay-pane').parentElement.style.zIndex = '1001';
    }
}
